/**
 * AI-Powered Template Recommendation Service
 * Analyzes job role, industry, and user preferences to recommend optimal template and styling
 */

/* eslint-disable lingui/no-unlocalized-strings */

import { i18n } from "@lingui/core";
import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import { templatesList } from "@stylingcv/utils";

// Template metadata with industry scoring
type TemplateProfile = {
  name: string;
  style: "modern" | "classic" | "creative" | "minimal" | "professional" | "academic";
  industries: string[];
  experienceLevel: ("entry" | "mid" | "senior" | "executive")[];
  layoutType: "single-column" | "two-column" | "mixed";
  visualComplexity: "simple" | "moderate" | "complex";
  colorCompatibility: "conservative" | "balanced" | "bold";
  premiumFeatures: boolean;
};

// Industry-specific color palettes
type ColorPalette = {
  name: string;
  primary: string;
  background: string;
  text: string;
  description: string;
};

// Internal type for static palette data with message descriptors
type ColorPaletteData = {
  name: string;
  primary: string;
  background: string;
  text: string;
  description: MessageDescriptor;
};

// Template profiles for AI recommendation
const templateProfiles: Record<string, TemplateProfile> = {
  glalie: {
    name: "framed",
    style: "modern",
    industries: ["technology", "software", "startup", "design", "marketing"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  pikachu: {
    name: "highlight",
    style: "creative",
    industries: ["design", "creative", "media", "advertising", "arts"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "mixed",
    visualComplexity: "complex",
    colorCompatibility: "bold",
    premiumFeatures: false,
  },
  bronzor: {
    name: "classic",
    style: "professional",
    industries: ["finance", "consulting", "law", "healthcare", "corporate"],
    experienceLevel: ["mid", "senior", "executive"],
    layoutType: "single-column",
    visualComplexity: "simple",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  chikorita: {
    name: "split",
    style: "minimal",
    industries: ["technology", "research", "academic", "science"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "single-column",
    visualComplexity: "simple",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  ditto: {
    name: "essential",
    style: "classic",
    industries: ["education", "government", "non-profit", "traditional"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "single-column",
    visualComplexity: "simple",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  gengar: {
    name: "bold",
    style: "modern",
    industries: ["technology", "consulting", "business", "sales"],
    experienceLevel: ["mid", "senior"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  kakuna: {
    name: "simple",
    style: "professional",
    industries: ["corporate", "management", "operations", "logistics"],
    experienceLevel: ["mid", "senior", "executive"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  leafish: {
    name: "leafish",
    style: "creative",
    industries: ["environmental", "sustainability", "non-profit", "green-tech"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "single-column",
    visualComplexity: "moderate",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  nosepass: {
    name: "grid",
    style: "academic",
    industries: ["research", "academic", "science", "education"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "single-column",
    visualComplexity: "simple",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  onyx: {
    name: "onyx",
    style: "minimal",
    industries: ["technology", "engineering", "architecture", "design"],
    experienceLevel: ["mid", "senior"],
    layoutType: "two-column",
    visualComplexity: "simple",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  rhyhorn: {
    name: "straight",
    style: "professional",
    industries: ["construction", "manufacturing", "engineering", "operations"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "single-column",
    visualComplexity: "moderate",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  azurill: {
    name: "clean",
    style: "modern",
    industries: ["healthcare", "pharma", "medical", "wellness"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  feminine: {
    name: "feminine",
    style: "creative",
    industries: ["fashion", "beauty", "lifestyle", "hospitality", "retail"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "mixed",
    visualComplexity: "complex",
    colorCompatibility: "bold",
    premiumFeatures: false,
  },
  confidence: {
    name: "confidence",
    style: "professional",
    industries: ["technology", "management", "consulting", "project management", "business"],
    experienceLevel: ["mid", "senior", "executive"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  executive: {
    name: "executive",
    style: "professional",
    industries: ["corporate", "management", "consulting", "finance", "executive"],
    experienceLevel: ["senior", "executive"],
    layoutType: "two-column",
    visualComplexity: "simple",
    colorCompatibility: "conservative",
    premiumFeatures: false,
  },
  metro: {
    name: "metro",
    style: "modern",
    industries: ["creative", "design", "marketing", "media", "advertising"],
    experienceLevel: ["entry", "mid", "senior"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "balanced",
    premiumFeatures: false,
  },
  slate: {
    name: "slate",
    style: "creative",
    industries: ["technology", "design", "startup", "engineering", "creative"],
    experienceLevel: ["mid", "senior"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "bold",
    premiumFeatures: false,
  },
  horizon: {
    name: "horizon",
    style: "modern",
    industries: ["technology", "startup", "consulting", "business", "software"],
    experienceLevel: ["mid", "senior"],
    layoutType: "mixed",
    visualComplexity: "complex",
    colorCompatibility: "bold",
    premiumFeatures: false,
  },
  folio: {
    name: "folio",
    style: "professional",
    industries: ["design", "architecture", "creative", "consulting", "publishing"],
    experienceLevel: ["mid", "senior", "executive"],
    layoutType: "two-column",
    visualComplexity: "moderate",
    colorCompatibility: "balanced",
    premiumFeatures: true,
  },
  cipher: {
    name: "cipher",
    style: "modern",
    industries: ["technology", "engineering", "architecture", "consulting", "research"],
    experienceLevel: ["senior", "executive"],
    layoutType: "single-column",
    visualComplexity: "simple",
    colorCompatibility: "conservative",
    premiumFeatures: true,
  },
};

// Industry-specific color palettes with message descriptors
// Descriptions are resolved at runtime via getColorPalettes()
const industryColorPalettesData: Record<string, ColorPaletteData[]> = {
  technology: [
    {
      name: "Tech Blue",
      primary: "#2563eb",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Professional tech blue`,
    },
    {
      name: "Modern Purple",
      primary: "#7c3aed",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Innovative purple`,
    },
    {
      name: "Clean Slate",
      primary: "#475569",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Minimal gray`,
    },
  ],
  finance: [
    {
      name: "Trust Blue",
      primary: "#1e40af",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Reliable navy blue`,
    },
    {
      name: "Professional Gray",
      primary: "#374151",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Conservative gray`,
    },
    {
      name: "Success Green",
      primary: "#059669",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Growth green`,
    },
  ],
  creative: [
    {
      name: "Creative Orange",
      primary: "#ea580c",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Energetic orange`,
    },
    {
      name: "Artistic Purple",
      primary: "#9333ea",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Creative purple`,
    },
    {
      name: "Bold Red",
      primary: "#dc2626",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Bold statement red`,
    },
  ],
  healthcare: [
    {
      name: "Medical Blue",
      primary: "#2563eb",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Trustworthy medical blue`,
    },
    {
      name: "Healing Green",
      primary: "#059669",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Calming healthcare green`,
    },
    {
      name: "Care Teal",
      primary: "#0891b2",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Professional teal`,
    },
  ],
  design: [
    {
      name: "Designer Pink",
      primary: "#ec4899",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Creative pink`,
    },
    {
      name: "Modern Indigo",
      primary: "#6366f1",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Contemporary indigo`,
    },
    {
      name: "Artistic Amber",
      primary: "#f59e0b",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Warm amber`,
    },
  ],
  default: [
    {
      name: "Professional Blue",
      primary: "#2563eb",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Classic professional blue`,
    },
    {
      name: "Elegant Gray",
      primary: "#475569",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Sophisticated gray`,
    },
    {
      name: "Modern Teal",
      primary: "#0891b2",
      background: "#ffffff",
      text: "#1f2937",
      description: msg`Contemporary teal`,
    },
  ],
};

/**
 * Resolve color palette data to ColorPalette with translated descriptions
 */
function resolveColorPalette(data: ColorPaletteData): ColorPalette {
  return {
    name: data.name,
    primary: data.primary,
    background: data.background,
    text: data.text,
    description: i18n._(data.description),
  };
}

/**
 * Get color palettes for an industry with translated descriptions
 */
function getColorPalettes(industry: string): ColorPalette[] {
  const palettes = industryColorPalettesData[industry] ?? industryColorPalettesData.default;
  return palettes.map(resolveColorPalette);
}

// Job role to industry mapping
const jobRoleIndustryMap: Record<string, string> = {
  "software engineer": "technology",
  developer: "technology",
  "data scientist": "technology",
  "product manager": "technology",
  "ux designer": "design",
  "ui designer": "design",
  "graphic designer": "creative",
  "marketing manager": "creative",
  "financial analyst": "finance",
  accountant: "finance",
  consultant: "finance",
  nurse: "healthcare",
  doctor: "healthcare",
  physician: "healthcare",
  teacher: "education",
  professor: "academic",
  researcher: "academic",
  // Engineering roles
  "civil engineer": "engineering",
  "mechanical engineer": "engineering",
  "electrical engineer": "engineering",
  "structural engineer": "engineering",
  "chemical engineer": "engineering",
  "environmental engineer": "engineering",
  "project manager": "engineering",
  "construction manager": "engineering",
  // Other professional roles
  lawyer: "legal",
  attorney: "legal",
  "sales manager": "sales",
  "marketing specialist": "marketing",
  "hr manager": "human resources",
  "operations manager": "operations",
};

// Experience level detection from years
function getExperienceLevel(yearsOfExperience?: number): "entry" | "mid" | "senior" | "executive" {
  if (!yearsOfExperience || yearsOfExperience < 2) return "entry";
  if (yearsOfExperience < 5) return "mid";
  if (yearsOfExperience < 10) return "senior";
  return "executive";
}

// Industry detection from job role
function detectIndustry(jobRole: string): string {
  const lowerJobRole = jobRole.toLowerCase();

  for (const [role, industry] of Object.entries(jobRoleIndustryMap)) {
    if (lowerJobRole.includes(role)) {
      return industry;
    }
  }

  // Fallback industry detection
  if (
    lowerJobRole.includes("tech") ||
    lowerJobRole.includes("software") ||
    lowerJobRole.includes("developer")
  ) {
    return "technology";
  }
  if (lowerJobRole.includes("design") || lowerJobRole.includes("creative")) {
    return "design";
  }
  if (lowerJobRole.includes("finance") || lowerJobRole.includes("bank")) {
    return "finance";
  }
  if (lowerJobRole.includes("health") || lowerJobRole.includes("medical")) {
    return "healthcare";
  }
  if (
    lowerJobRole.includes("civil") ||
    lowerJobRole.includes("construction") ||
    lowerJobRole.includes("structural")
  ) {
    return "engineering";
  }

  return "default";
}

// Export the function for use in other components
export { detectIndustry };

// Template recommendation interface
type TemplateRecommendation = {
  templateName: string;
  score: number;
  reasons: string[];
  colorPalette: ColorPalette;
  confidence: "high" | "medium" | "low";
};

// AI Template Recommendation Service
export const TemplateRecommendationService = {
  /**
   * Get AI-powered template recommendations
   */
  getRecommendations(
    jobRole: string,
    yearsOfExperience?: number,
    preferredStyle?: string,
    industry?: string,
  ): TemplateRecommendation[] {
    const detectedIndustry = industry ?? detectIndustry(jobRole);
    const experienceLevel = getExperienceLevel(yearsOfExperience);

    const recommendations: TemplateRecommendation[] = [];

    // Score each template
    for (const template of templatesList) {
      const profile = templateProfiles[template];

      // Skip if template profile is not defined
      if (!profile) {
        console.warn(`Template profile not found for: ${template}`);
        continue;
      }

      let score = 0;
      const reasons: string[] = [];

      // Industry match (40% weight)
      if (profile.industries.includes(detectedIndustry)) {
        score += 40;
        reasons.push(`Perfect fit for ${detectedIndustry} industry`);
      } else if (profile.industries.includes("default") || detectedIndustry === "default") {
        score += 20;
        reasons.push("Suitable for general professional use");
      }

      // Experience level match (30% weight)
      if (profile.experienceLevel.includes(experienceLevel)) {
        score += 30;
        reasons.push(`Ideal for ${experienceLevel}-level professionals`);
      }

      // Style preference match (20% weight)
      if (preferredStyle && profile.style === preferredStyle) {
        score += 20;
        reasons.push(`Matches your ${preferredStyle} style preference`);
      } else {
        score += 10; // Base style score
      }

      // Layout and complexity bonus (10% weight)
      if (experienceLevel === "entry" && profile.visualComplexity === "simple") {
        score += 10;
        reasons.push("Clean, simple design perfect for early career");
      } else if (experienceLevel === "senior" && profile.visualComplexity === "moderate") {
        score += 10;
        reasons.push("Sophisticated design for experienced professionals");
      } else if (experienceLevel === "executive" && profile.visualComplexity === "simple") {
        score += 10;
        reasons.push("Executive-level minimalism and elegance");
      }

      // Get recommended color palette with runtime translation
      const colorPalettes = getColorPalettes(detectedIndustry);
      const colorPalette = colorPalettes[0]; // Default to first palette

      // Determine confidence level
      let confidence: "high" | "medium" | "low" = "low";
      if (score >= 70) confidence = "high";
      else if (score >= 50) confidence = "medium";

      recommendations.push({
        templateName: template,
        score,
        reasons,
        colorPalette,
        confidence,
      });
    }

    // Sort by score and return top recommendations
    return recommendations.sort((a, b) => b.score - a.score).slice(0, 5); // Return top 5 recommendations
  },

  /**
   * Get color palette recommendations for an industry
   */
  getColorRecommendations(industry: string): ColorPalette[] {
    return getColorPalettes(industry);
  },

  /**
   * Get template profile information
   */
  getTemplateProfile(templateName: string): TemplateProfile | null {
    return templateProfiles[templateName] ?? null;
  },

  /**
   * Get industry from job role
   */
  detectIndustryFromJobRole(jobRole: string): string {
    return detectIndustry(jobRole);
  },
};

export type { ColorPalette, TemplateProfile, TemplateRecommendation };
